import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import AsyncSelect from 'react-select/async';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { VariableSizeList as List } from 'react-window';
import { RC, dateInp, refreshBtn, excelBtn } from './ReportShell';

// ─── Constants ────────────────────────────────────────────────────────────────
const COLUMNS = [
    { label: 'Code', align: 'left' },
    { label: 'Item', align: 'left' },
    { label: 'Opening', align: 'right' },
    { label: 'Purchase', align: 'right' },
    { label: 'Purch.Rtn', align: 'right' },
    { label: 'Sale', align: 'right' },
    { label: 'Sale Rtn', align: 'right' },
    { label: 'Stk Journal', align: 'right' },
    { label: 'Closing', align: 'right' },
    { label: 'Re-Order', align: 'right' },
    { label: 'Purch Rate', align: 'right' },
    { label: 'MRP', align: 'right' },
    { label: 'GST%', align: 'right' },
    { label: 'HSN Code', align: 'left' },
    { label: 'Last Sale Date', align: 'left' },
    { label: 'Last Sale Qty', align: 'right' },
];
const COL_WIDTHS = [80, 210, 70, 80, 80, 70, 70, 90, 70, 80, 90, 70, 50, 90, 110, 90];
const TOTAL_WIDTH = COL_WIDTHS.reduce((a, b) => a + b, 0);
const ROW_H = 28;
const CAT_H = 26;
const HEADER_H = 32;
const FOOTER_H = 30;

// ─── Shared cell base style ───────────────────────────────────────────────────
const cell = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '11px',
    padding: '0 6px',
    height: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    borderRight: '1px solid #e5e7eb',
    flexShrink: 0,
};

// ─── Header ───────────────────────────────────────────────────────────────────
const HeaderRow = React.memo(() => (
    <div style={{ display: 'flex', height: HEADER_H, width: TOTAL_WIDTH, background: '#1e293b' }}>
        {COLUMNS.map((col, i) => (
            <div key={col.label} style={{
                ...cell,
                width: COL_WIDTHS[i],
                justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                color: '#fff', fontWeight: 700, borderRight: '1px solid #334155',
            }}>
                {col.label}
            </div>
        ))}
    </div>
));

// ─── Category row ─────────────────────────────────────────────────────────────
const CategoryRow = React.memo(({ category }) => (
    <div style={{
        display: 'flex', alignItems: 'center',
        width: TOTAL_WIDTH, height: CAT_H,
        background: 'rgba(20,184,166,0.08)',
        borderBottom: '1px solid #ccfbf1',
        paddingLeft: 10, fontWeight: 700, fontSize: '11px', color: '#0d9488',
    }}>
        ▸ {category}
    </div>
));

// ─── Data row ─────────────────────────────────────────────────────────────────
const DataRow = React.memo(({ data, rowIndex }) => {
    const bg = data.ClosingQty < data.ReOrderLevelQty
        ? '#fff0f0'
        : data.ClosingQty === data.ReOrderLevelQty
            ? '#f7ffe0'
            : rowIndex % 2 === 0 ? '#ffffff' : '#f8fffe';

    const vals = [
        data.Code, data.Name,
        data.OpeningQty, data.PurchaseQty, data.PurchaseReturnQty,
        data.SaleQty, data.SaleReturnQty, data.StockJournalQty,
        data.ClosingQty, data.ReOrderLevelQty,
        (data.PurchaseRate ?? 0).toFixed(2), (data.MRP ?? 0).toFixed(2),
        data.GSTRate, data.HSNCode, data.LastSaleDate, data.LastSaleQty,
    ];

    const closingColor = data.ClosingQty > 0
        ? '#16a34a'
        : data.ClosingQty < 0
            ? '#dc2626'
            : '#64748b';

    return (
        <div style={{ display: 'flex', height: ROW_H, width: TOTAL_WIDTH, background: bg, borderBottom: '1px solid #f0f0f0' }}>
            {vals.map((v, i) => (
                <div key={i} style={{
                    ...cell,
                    width: COL_WIDTHS[i],
                    justifyContent: COLUMNS[i].align === 'right' ? 'flex-end' : 'flex-start',
                    color: i === 8 ? closingColor : 'inherit',
                    fontWeight: i === 8 ? 600 : 400,
                }}>
                    {v}
                </div>
            ))}
        </div>
    );
});

// ─── Footer / Total row ───────────────────────────────────────────────────────
const FooterRow = React.memo(({ totalsMap }) => (
    <div style={{ display: 'flex', height: FOOTER_H, width: TOTAL_WIDTH, background: '#1e293b', borderTop: '2px solid #334155' }}>
        {COLUMNS.map((col, i) => {
            const v = totalsMap[i];
            return (
                <div key={i} style={{
                    ...cell,
                    width: COL_WIDTHS[i],
                    justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                    color: v !== undefined ? '#fbbf24' : '#64748b',
                    fontWeight: 700,
                    borderRight: '1px solid #334155',
                }}>
                    {i === 0 ? 'TOTAL' : v !== undefined ? v : ''}
                </div>
            );
        })}
    </div>
));

// ─── Main component ───────────────────────────────────────────────────────────
function StockAll() {
    const [dataItem, setDataItem] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [searchQuery, setSearch] = useState('');
    const [ItemId, setItem] = useState('0');
    const [CategoryId, setCategory] = useState('0');
    const [loading, setLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCategory, setSelectedCat] = useState(null);
    // body height for the virtualised list
    const [bodyH, setBodyH] = useState(400);

    const listRef = useRef();
    const toolbarRef = useRef();
    const wrapperRef = useRef();

    const { isAuthenticated, companyId, token, username, logout, permissions } = useAuth();
    const hasReadPerm = permissions.includes('Read Reports');

    // ── Measure available body height ─────────────────────────────────────────
    useEffect(() => {
        const calc = () => {
            const wrapH = wrapperRef.current?.clientHeight ?? window.innerHeight;
            const toolbarH = toolbarRef.current?.clientHeight ?? 46;
            const available = wrapH - 12 - toolbarH - 6 - 2 - HEADER_H - FOOTER_H;
            setBodyH(Math.max(available, 80));
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, []);

    // ── Dates ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setFromDate(start.toISOString().split('T')[0]);
        setToDate(now.toISOString().split('T')[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, companyId, token, username]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchTableData = useCallback(async () => {
        if (!hasReadPerm) return;
        setDataItem([]);
        setLoading(true);
        try {
            // StoreId = '0' means all store locations
            const res = await fetchData('ReportApi',
                `/StockAll/Stock/0/${companyId}/${ItemId}/${CategoryId}/${fromDate}/${toDate}`, token);
            if (res.responseCode === '1111') {
                if (res.response_data?.length > 0) {
                    console.log('[StockAll] API fields:', Object.keys(res.response_data[0]));
                }
                setDataItem(res.response_data);
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, ItemId, CategoryId, fromDate, toDate, token, hasReadPerm]);

    const fetchItemOptions = async (q = '') => {
        if (q.length <= 1) return [];
        try {
            const r = await fetchData('PurchaseApi', `/Item/${companyId}/${q}`, token);
            if (r.responseCode === '1111' && Array.isArray(r.response_data))
                return r.response_data.map(x => ({ value: x.Id, label: x.ItemName1 }));
        } catch (e) { console.error(e); }
        return [];
    };

    const fetchCatOptions = async (q = '') => {
        if (q.length <= 1) return [];
        try {
            const r = await fetchData('PurchaseApi', `/Category/${companyId}/${q}`, token);
            if (r.responseCode === '1111' && Array.isArray(r.response_data))
                return r.response_data.map(x => ({ value: x.Id, label: x.Name }));
        } catch (e) { console.error(e); }
        return [];
    };

    // ── Derived data ──────────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        if (!searchQuery) return dataItem;
        const q = searchQuery.toLowerCase();
        return dataItem.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(q)));
    }, [dataItem, searchQuery]);

    const flatRows = useMemo(() => {
        const rows = [];
        const groups = {};
        filtered.forEach(item => {
            const cat = item.CategoryName || item.categoryName || item.Category || item.category || '(No Category)';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(item);
        });
        let idx = 0;
        Object.entries(groups).forEach(([cat, items]) => {
            rows.push({ type: 'cat', cat });
            items.forEach(item => rows.push({ type: 'row', data: item, idx: idx++ }));
        });
        return rows;
    }, [filtered]);

    useEffect(() => {
        if (listRef.current) listRef.current.resetAfterIndex(0, false);
    }, [flatRows]);

    const getSize = useCallback(i => flatRows[i]?.type === 'cat' ? CAT_H : ROW_H, [flatRows]);

    const totals = useMemo(() => {
        let op = 0, p = 0, pr = 0, s = 0, sr = 0, sj = 0, cl = 0;
        dataItem.forEach(r => {
            op += r.OpeningQty;
            p += r.PurchaseQty;
            pr += r.PurchaseReturnQty;
            s += r.SaleQty;
            sr += r.SaleReturnQty;
            sj += r.StockJournalQty;
            cl += r.ClosingQty;
        });
        return {
            2: op.toFixed(2),
            3: p.toFixed(2),
            4: pr.toFixed(2),
            5: s.toFixed(2),
            6: sr.toFixed(2),
            7: sj.toFixed(2),
            8: cl.toFixed(2),
        };
    }, [dataItem]);

    // ── Row renderer ──────────────────────────────────────────────────────────
    const RowRenderer = useCallback(({ index, style }) => {
        const r = flatRows[index];
        return (
            <div style={{ ...style, width: TOTAL_WIDTH }}>
                {r.type === 'cat'
                    ? <CategoryRow category={r.cat} />
                    : <DataRow data={r.data} rowIndex={r.idx} />}
            </div>
        );
    }, [flatRows]);

    // ── Excel export ──────────────────────────────────────────────────────────
    const fmt = d => new Date(d).toLocaleDateString('en-GB');

    const handleExcel = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Stock All');
        const now = new Date();
        ws.mergeCells('A1:L1');
        const t = ws.getCell('A1');
        t.value = `Stock All ${fmt(fromDate)} – ${fmt(toDate)} | ${now.toLocaleString()} | ${username}`;
        t.font = { bold: true, size: 12 }; t.alignment = { horizontal: 'center' };
        t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
        ws.addRow([]);
        // Columns: A=Category B=Code C=Item D=Opening E=Purchase F=Purch.Rtn G=Sale H=Sale Rtn I=Stk Journal J=Closing K=Purch Rate L=MRP M=GST% N=HSN O=Last Sale Date P=Last Sale Qty
        const hdr = ws.addRow(['Category', 'Code', 'Item', 'Opening', 'Purchase', 'Purch.Rtn', 'Sale', 'Sale Rtn', 'Stk Journal', 'Closing', 'Purch Rate', 'MRP', 'GST%', 'HSN', 'Last Sale Date', 'Last Sale Qty']);
        hdr.eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } }; c.alignment = { horizontal: 'center' }; c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; });
        filtered.forEach(d => {
            const row = ws.addRow([d.CategoryName, d.Code, d.Name, d.OpeningQty, d.PurchaseQty, d.PurchaseReturnQty, d.SaleQty, d.SaleReturnQty, d.StockJournalQty, d.ClosingQty, parseFloat((d.PurchaseRate ?? 0).toFixed(2)), parseFloat((d.MRP ?? 0).toFixed(2)), d.GSTRate, d.HSNCode, d.LastSaleDate, d.LastSaleQty]);
            row.eachCell((c, n) => { c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; if (n >= 4 && n <= 16) { c.alignment = { horizontal: 'right' }; if (n >= 4 && n <= 12) c.numFmt = '#,##0.00'; } });
        });
        // totals: D=Opening(2) E=Purchase(3) F=Purch.Rtn(4) G=Sale(5) H=Sale Rtn(6) I=Stk Journal(7) J=Closing(8)
        const tr = ws.addRow(['Total', '', '', totals[2], totals[3], totals[4], totals[5], totals[6], totals[7], totals[8], '', '', '', '', '', '']);
        tr.eachCell((c, n) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } }; c.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } }; if (n >= 4 && n <= 10) { c.alignment = { horizontal: 'right' }; c.numFmt = '#,##0.00'; } });
        [15, 10, 22, 10, 10, 10, 10, 10, 12, 10, 12, 10, 8, 12, 15, 12].forEach((w, i) => ws.getColumn(i + 1).width = w);
        saveAs(new Blob([await wb.xlsx.writeBuffer()]), `StockAll_${fmt(toDate)}.xlsx`);
    };

    // ── shared select styles ──────────────────────────────────────────────────
    const selSx = {
        control: b => ({ ...b, minHeight: 26, height: 26, fontSize: 11, borderRadius: 6, borderColor: 'rgba(0,0,0,0.12)', boxShadow: 'none', '&:hover': { borderColor: RC.accent } }),
        valueContainer: b => ({ ...b, height: 26, padding: '0 6px' }),
        indicatorsContainer: b => ({ ...b, height: 26 }),
        dropdownIndicator: b => ({ ...b, padding: '0 4px' }),
        clearIndicator: b => ({ ...b, padding: '0 2px' }),
        placeholder: b => ({ ...b, fontSize: 11, color: '#9ca3af' }),
        singleValue: b => ({ ...b, fontSize: 11 }),
        menu: b => ({ ...b, fontSize: 11, zIndex: 9999 }),
        menuPortal: b => ({ ...b, zIndex: 9999 }),
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div ref={wrapperRef} style={{ overflow: 'hidden', fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: RC.bg, padding: '6px', boxSizing: 'border-box' }}>

            {/* ── Toolbar ── */}
            <div ref={toolbarRef} style={{ backgroundColor: RC.card, borderRadius: 8, boxShadow: RC.shadow, border: `1px solid ${RC.cardBorder}`, marginBottom: 6, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '5px 10px', gap: 6, flexWrap: 'nowrap', overflowX: 'auto' }}>

                    {/* Title */}
                    <span style={{ fontWeight: 800, fontSize: 12, color: RC.text, fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', marginRight: 4 }}>
                        STOCK ALL
                    </span>
                    <span style={{ fontSize: 10, color: RC.muted, backgroundColor: RC.accentDim, border: `1px solid ${RC.accentBorder}`, borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap', marginRight: 6 }}>
                        {filtered.length} records
                    </span>

                    {/* Category */}
                    <div style={{ width: 150, flexShrink: 0 }}>
                        <AsyncSelect cacheOptions loadOptions={fetchCatOptions}
                            onChange={opt => { setSelectedCat(opt); setCategory(opt?.value || '0'); setDataItem([]); }}
                            value={selectedCategory} placeholder="Category" isSearchable
                            menuPortalTarget={document.body} styles={selSx} />
                    </div>

                    {/* Item */}
                    <div style={{ width: 150, flexShrink: 0 }}>
                        <AsyncSelect cacheOptions loadOptions={fetchItemOptions}
                            onChange={opt => { setSelectedItem(opt); setItem(opt?.value || '0'); setDataItem([]); }}
                            value={selectedItem} placeholder="Item" isSearchable
                            menuPortalTarget={document.body} styles={selSx} />
                    </div>

                    {/* Dates */}
                    <span style={{ fontSize: 10, color: RC.muted, whiteSpace: 'nowrap' }}>From</span>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ ...dateInp, width: 120, height: 26, fontSize: 11 }} />
                    <span style={{ fontSize: 10, color: RC.muted, whiteSpace: 'nowrap' }}>To</span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ ...dateInp, width: 120, height: 26, fontSize: 11 }} />

                    {/* Search */}
                    <input type="text" placeholder="Search…" value={searchQuery} onChange={e => setSearch(e.target.value)}
                        style={{ ...dateInp, width: 130, height: 26, fontSize: 11 }} />

                    {/* Spacer */}
                    <div style={{ flex: 1 }} />

                    {/* Actions */}
                    <button onClick={fetchTableData} style={{ ...refreshBtn, height: 26, fontSize: 11, padding: '0 10px', flexShrink: 0 }}>Refresh</button>
                    <button onClick={handleExcel} style={{ ...excelBtn, height: 26, fontSize: 11, padding: '0 10px', flexShrink: 0 }}>Excel</button>
                </div>
            </div>

            {/* ── Table card — fills remaining height ── */}
            <div style={{ backgroundColor: RC.card, borderRadius: 8, boxShadow: RC.shadow, border: `1px solid ${RC.cardBorder}`, flex: 1, overflow: 'hidden' }}>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: 24, height: 24, border: `3px solid ${RC.accentBorder}`, borderTopColor: RC.accent, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                        <div style={{ width: TOTAL_WIDTH }}>
                        {/* HEADER */}
                        <HeaderRow />
                        {/* BODY */}
                        <List
                            ref={listRef}
                            height={bodyH}
                            itemCount={flatRows.length}
                            itemSize={getSize}
                            width={TOTAL_WIDTH}
                            overscanCount={20}
                            style={{ overflowX: 'hidden', overflowY: 'auto', height: '575px' }}
                        >
                            {RowRenderer}
                        </List>
                        {/* FOOTER — always visible right below the list */}
                        <FooterRow totalsMap={totals} />
                        </div>
                    </div>
                )}
            </div>

            {/* spinner keyframe */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

export default StockAll;
